/**
 * judgeContent.js - Door Knock's half of the judgement.
 *
 *   Phase 2  A/B/C  the ENGINE's, from the trial record. This game sends none.
 *   Phase 3  A/B/C/D  carrier, creature, combination, spontaneous
 *   Phase 4  A/B/D    verb morphology, prepositions, integration
 *                     C (Articles) is observational - never sent, never scored
 *
 * The matching itself is the platform's (../../shared/phrase.js), because
 * "did the child say it, allowing for what the recogniser does to a child's
 * voice" is not a question about doors.
 */

import { trialShape } from '../../../../engine/judgement.js';
import { tokenise } from '../../../../engine/judgement.js';
import { producedAny, orderedCombination, candidates } from '../../shared/phrase.js';
import {
  acceptedPhrases, canonicalPhrase, getCreature, getCarrier, getPlace, getVerb,
  CARRIERS, VERBS, PLACES,
} from '../content/creatures.js';

/**
 * @param {object} input
 * @param {number} input.phase
 * @param {{creatureId:string, carrierId?:string, verbId?:string, placeId?:string}} input.target
 * @param {{creatures:string[], carriers?:string[], places?:string[]}} [input.choices]
 * @param {object|null} [input.trial] NORMALISED trial record
 * @param {string|null} [input.transcript]
 * @param {Array} [input.alternatives]
 */
export function judgeAttempt({ phase, target, choices = null, trial = null, transcript = null, alternatives = [] }) {
  const expected = canonicalPhrase(target);

  if (phase === 2) {
    // Every box here is the engine's, judged from the ladder walk. The game
    // still needs to know whether the door opens.
    const hit = producedAny({ phase, targets: acceptedPhrases(target), transcript, alternatives });
    return {
      components: {},
      success: hit.produced,
      expected,
      heard: hit.matched,
      detail: { judgedBy: 'engine', reason: hit.reason },
    };
  }

  if (phase === 3) return judgePhase3({ target, choices, trial, transcript, alternatives, expected });
  if (phase === 4) return judgePhase4({ target, choices, trial, transcript, alternatives, expected });

  throw new Error(
    `Door Knock covers phases 2-4; asked to judge phase ${phase}. A game must `
    + 'never guess at a phase it does not implement - it would write a box that '
    + 'means something different from every other game.'
  );
}

/* ---------------------------------------------------------------------------
 * PHASE 3 - the same four boxes as any manding level, and the child chooses.
 * ------------------------------------------------------------------------- */
function judgePhase3({ target, choices, trial, transcript, alternatives, expected }) {
  const creatures = (choices && choices.creatures && choices.creatures.length)
    ? choices.creatures
    : [target.creatureId];
  // The closed set is the framework's, not the level's on-screen offer: a child
  // who says "get frog" on a level showing "want" has still used a carrier.
  const carriers = Object.keys(CARRIERS);

  let best = { rank: -1, creatureId: null, carrierId: null, combo: null };
  for (const carrierId of carriers) {
    for (const creatureId of creatures) {
      const combo = orderedCombination({
        phase: 3,
        carrierWords: getCarrier(carrierId).accept,
        nounWords: getCreature(creatureId).accept,
        transcript, alternatives,
      });
      const rank = combo.ordered ? 3 : (combo.carrier && combo.noun) ? 2 : combo.noun ? 1 : combo.carrier ? 0 : -1;
      if (rank > best.rank) best = { rank, creatureId, carrierId, combo };
      if (rank === 3) break;
    }
    if (best.rank === 3) break;
  }

  const combo = best.combo || { carrier: false, noun: false, ordered: false, candidate: transcript, reason: 'Nothing was asked for.' };
  const creature = getCreature(best.creatureId) || getCreature(target.creatureId);
  const carrier = getCarrier(best.carrierId) || getCarrier(target.carrierId) || getCarrier('want');
  const asked = combo.ordered ? `${carrier.word} ${creature.word}` : expected;

  const shape = trial ? trialShape(trial) : null;
  const echoed = shape ? shape.fullModelBeforeOnset : false;

  return {
    components: {
      A: { success: combo.carrier, expected: carrier.word, heard: combo.carrierWord,
           reason: combo.carrier ? `Used "${combo.carrierWord}".` : 'No action word.' },
      B: { success: combo.noun, expected: creature.word, heard: combo.nounWord,
           reason: combo.noun ? `Named "${combo.nounWord}".` : 'No creature was named.' },
      C: { success: combo.ordered, expected: asked, heard: combo.candidate, reason: combo.reason },
      D: { success: combo.ordered && !echoed, expected: asked, heard: combo.candidate,
           reason: !combo.ordered ? 'No two-word request to credit.'
             : echoed ? 'Pip had said the phrase before the child spoke.'
             : 'Asked before Pip modelled it.',
           modelWasAvailable: Boolean(trial && (trial.modelScheduled || trial.modelPlayed)),
           priorModelAgoMs: trial ? trial.priorModelAgoMs : null },
    },
    success: combo.ordered,
    expected: asked,
    heard: combo.candidate,
    detail: { creatureId: best.creatureId, carrier: carrier.id, effect: carrier.effect, echoed },
  };
}

/* ---------------------------------------------------------------------------
 * PHASE 4 - Early Grammar.
 *
 *   A  Verb Morphology  an -ing form is present
 *   B  Prepositions     a preposition from the closed set is present
 *   C  Articles         OBSERVATIONAL. The recogniser inserts and deletes "the"
 *                       on its own, so this box measures Chrome. Never sent.
 *   D  Integration      all of it in one utterance, in order
 *
 * The framework has no "object label" box at Phase 4, so a child could say
 * "putting in" with no creature at all and score A and B. Integration is where
 * that is caught: D requires the creature too. That is this game's reading of
 * "combines grammar elements", and it is a judgement another game could make
 * differently - which is precisely the kind of thing the contract should
 * settle rather than leave to each author.
 * ------------------------------------------------------------------------- */
function judgePhase4({ target, choices, trial, transcript, alternatives, expected }) {
  const creatures = (choices && choices.creatures && choices.creatures.length)
    ? choices.creatures : [target.creatureId];
  const places = (choices && choices.places && choices.places.length)
    ? choices.places : Object.keys(PLACES);

  const verbWords = Object.values(VERBS).flatMap((v) => v.accept.filter((w) => !w.includes(' ')));
  const placeWords = places.flatMap((id) => getPlace(id).accept);

  let found = { verb: null, creature: null, place: null, candidate: null, ordered: false };

  for (const candidate of candidates({ phase: 4, transcript, alternatives })) {
    const tokens = tokenise(candidate);
    const vi = tokens.findIndex((t) => verbWords.includes(t));
    const ci = tokens.findIndex((t) => creatures.some((id) => getCreature(id).accept.includes(t)));
    const pi = tokens.findIndex((t) => placeWords.includes(t));

    if (found.verb === null && vi !== -1) found.verb = tokens[vi];
    if (found.creature === null && ci !== -1) found.creature = tokens[ci];
    if (found.place === null && pi !== -1) found.place = tokens[pi];
    found.candidate = found.candidate || candidate;

    if (vi !== -1 && ci !== -1 && pi !== -1 && vi < ci && ci < pi) {
      found = { verb: tokens[vi], creature: tokens[ci], place: tokens[pi], candidate, ordered: true };
      break;
    }
  }

  return {
    components: {
      A: { success: found.verb !== null, expected: getVerb(target.verbId).word, heard: found.verb,
           reason: found.verb ? `Used the -ing form "${found.verb}".` : 'No -ing verb.' },
      B: { success: found.place !== null, expected: getPlace(target.placeId).word, heard: found.place,
           reason: found.place ? `Used the preposition "${found.place}".` : 'No preposition.' },
      D: { success: found.ordered, expected, heard: found.candidate,
           reason: found.ordered
             ? 'Verb, creature and preposition, in one utterance and in order.'
             : 'The pieces were not all there in one go.' },
    },
    success: found.ordered,
    expected,
    heard: found.candidate,
    detail: { verb: found.verb, creature: found.creature, place: found.place },
  };
}

/** The component codes this game submits, per phase. */
export function gameJudgedCodes(phase) {
  if (phase === 2) return [];
  if (phase === 3) return ['A', 'B', 'C', 'D'];
  if (phase === 4) return ['A', 'B', 'D'];
  return [];
}

export default { judgeAttempt, gameJudgedCodes };
